import type { MenuItem, SelectedOption } from "./contracts";

export type MenuCategory = {
  id: string;
  name: string;
  active: boolean;
  sortOrder: number;
};

export type MenuDocument = {
  categories: MenuCategory[];
  items: MenuItem[];
};

export type MenuRevisionStatus = "draft" | "published" | "archived";

export type MenuRevision = {
  id: string;
  storeId: string;
  revision: number;
  status: MenuRevisionStatus;
  categories: MenuCategory[];
  items: MenuItem[];
  createdAt: string;
  createdBy: string;
  publishedAt?: string;
};

export type PublishedMenu = {
  revisionId: string;
  revision: number;
  publishedAt: string;
  categories: MenuCategory[];
  items: MenuItem[];
};

export type MenuRevisionInput = {
  categories?: MenuCategory[];
  items: MenuItem[];
};

export type MenuDeviceSyncStatus = {
  deviceId: string;
  clientId: string;
  label: string;
  active: boolean;
  lastSeenAt?: string;
  syncedRevisionId?: string;
  syncedRevision?: number;
  syncedAt?: string;
  status: "synced" | "pending" | "never";
};

export type MenuSyncStatusResponse = {
  published: { revisionId: string; revision: number; publishedAt: string } | null;
  devices: MenuDeviceSyncStatus[];
};

export function deriveMenuCategories(items: MenuItem[]): MenuCategory[] {
  const categories = new Map<string, MenuCategory>();
  for (const item of items) {
    const id = item.category.trim();
    if (!id || categories.has(id)) continue;
    categories.set(id, { id, name: id, active: true, sortOrder: categories.size });
  }
  return [...categories.values()];
}

export function normalizeMenuDocument(input: MenuRevisionInput | MenuItem[]): MenuDocument {
  const items = Array.isArray(input) ? input : input.items;
  const hasExplicitCategories = !Array.isArray(input) && Object.prototype.hasOwnProperty.call(input, "categories");
  const categories = hasExplicitCategories ? ((input as MenuRevisionInput).categories ?? []) : deriveMenuCategories(items);
  return { categories: categories.map((category, index) => ({ ...category, sortOrder: Number.isSafeInteger(category.sortOrder) ? category.sortOrder : index })), items };
}

export function parseMenuDocument(value: string): MenuDocument {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) return normalizeMenuDocument(parsed as MenuItem[]);
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as { items?: unknown }).items)) {
      const items = (parsed as { items: MenuItem[] }).items;
      const categories = (parsed as { categories?: unknown }).categories;
      return Array.isArray(categories)
        ? normalizeMenuDocument({ items, categories: categories as MenuCategory[] })
        : normalizeMenuDocument({ items });
    }
  } catch {
    // Invalid persisted documents are handled by the caller's validation path.
  }
  return { categories: [], items: [] };
}

/**
 * Turn UI-selected option ids into an order-safe snapshot.
 *
 * The menu is server-authored, but the selection still has to be validated at
 * the local order boundary so a stale or tampered UI cannot select an inactive
 * option, exceed a single-select group, or omit a required group.
 */
export function selectedOptionsForMenuItem(item: MenuItem, selectedIdsByGroup: Record<string, string[]>): SelectedOption[] {
  const selected: SelectedOption[] = [];
  const seenGroups = new Set<string>();
  for (const group of item.optionGroups ?? []) {
    if (seenGroups.has(group.id)) throw new Error(`Duplicate menu option group: ${group.id}`);
    seenGroups.add(group.id);
    const ids = [...new Set(selectedIdsByGroup[group.id] ?? [])];
    if (group.required && ids.length === 0) throw new Error(`${group.name} is required`);
    if (!group.multiple && ids.length > 1) throw new Error(`${group.name} allows only one option`);
    const options = new Map(group.options.map((option) => [option.id, option]));
    for (const id of ids) {
      const option = options.get(id);
      if (!option) throw new Error(`Unknown option in ${group.name}`);
      if (!option.active) throw new Error(`${option.name} is unavailable`);
      selected.push({ groupId: group.id, optionId: option.id, name: `${group.name}: ${option.name}`, priceMinor: option.priceMinor });
    }
  }
  const knownGroups = new Set((item.optionGroups ?? []).map((group) => group.id));
  for (const groupId of Object.keys(selectedIdsByGroup)) {
    if (!knownGroups.has(groupId) && (selectedIdsByGroup[groupId] ?? []).length > 0) throw new Error(`Unknown menu option group: ${groupId}`);
  }
  return selected;
}
